import { Routes } from '@angular/router';
import {
  APP_ROUTE_PATHS,
  customerDetailsMatcher,
  personDetailsMatcher,
  projectDetailsMatcher,
  teamDetailsMatcher,
} from './app-route-paths';
import { authGuard } from '../../guard/auth.guard';
import { loginGuard } from '../../guard/login.guard';
import { unsavedChangesGuard } from '../../guard/unsaved-changes.guard';
import { pageAccessData, pageAccessGuard } from '../../guard/page-access.guard';
import { LANDING_DATA_KEY, landingGuard } from './landing.resolver';
import { BUILT_IN_ADMINISTRATOR, PageRequirement } from '../../../shared/store/models/application-access.model';
import { OfferReportKind } from '../../../api/swagger/model/offerReportKind';

const guarded = (requirement: PageRequirement, data: Record<string, unknown> = {}) => ({
  canActivate: [pageAccessGuard],
  data: { ...data, ...pageAccessData(requirement) },
});

const landing = () => ({ canActivate: [landingGuard], data: { [LANDING_DATA_KEY]: true }, children: [] });

const loadOfferReport = () =>
  import('../../../pages/management/offer-report/offer-report.component').then((m) => m.OfferReportComponent);

export const appRoutes: Routes = [
  {
    path: APP_ROUTE_PATHS.authCallback,
    loadComponent: () =>
      import('src/app/shell/authorization/callback/auth-callback.component').then((m) => m.AuthCallbackComponent),
  },
  {
    path: APP_ROUTE_PATHS.legacyAuthCallback,
    loadComponent: () =>
      import('src/app/shell/authorization/callback/auth-callback.component').then((m) => m.AuthCallbackComponent),
  },
  {
    path: APP_ROUTE_PATHS.authLogoutCallback,
    loadComponent: () =>
      import('src/app/shell/authorization/callback/auth-logout-callback.component').then(
        (m) => m.AuthLogoutCallbackComponent
      ),
  },
  {
    path: APP_ROUTE_PATHS.legacyAuthLogoutCallback,
    loadComponent: () =>
      import('src/app/shell/authorization/callback/auth-logout-callback.component').then(
        (m) => m.AuthLogoutCallbackComponent
      ),
  },
  {
    path: APP_ROUTE_PATHS.signedOut,
    loadComponent: () =>
      import('src/app/shell/authorization/callback/auth-logout-callback.component').then(
        (m) => m.AuthLogoutCallbackComponent
      ),
  },
  {
    path: APP_ROUTE_PATHS.login,
    canActivate: [loginGuard],
    loadComponent: () => import('src/app/shell/authorization/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: APP_ROUTE_PATHS.root,
    loadComponent: () => import('src/app/shell/application-shell.component').then((m) => m.ApplicationShellComponent),
    canActivate: [authGuard],
    children: [
      // @template-demo:start root-redirect
      { path: APP_ROUTE_PATHS.root, pathMatch: 'full', ...landing() },
      // @template-demo:end root-redirect
      {
        path: APP_ROUTE_PATHS.myTasks,
        ...guarded('my-tasks'),
        loadComponent: () => import('../../../pages/my-tasks/my-tasks.component').then((m) => m.MyTasksComponent),
      },
      {
        path: APP_ROUTE_PATHS.planning,
        ...guarded('planning'),
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('../../../pages/planning/planning.component').then((m) => m.PlanningComponent),
      },
      {
        path: APP_ROUTE_PATHS.projects,
        ...guarded('projects'),
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('../../../pages/projects/projects.component').then((m) => m.ProjectsComponent),
      },
      {
        path: APP_ROUTE_PATHS.customers,
        ...guarded('customers'),
        loadComponent: () => import('../../../pages/customers/customers.component').then((m) => m.CustomersComponent),
      },
      {
        // After the static `customers` route so the directory still wins.
        matcher: customerDetailsMatcher,
        ...guarded('customers'),
        loadComponent: () =>
          import('../../../pages/customers/customer-details/customer-details.component').then(
            (m) => m.CustomerDetailsComponent
          ),
      },
      {
        path: APP_ROUTE_PATHS.teams,
        ...guarded('teams'),
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('../../../pages/teams/teams.component').then((m) => m.TeamsComponent),
      },
      {
        path: APP_ROUTE_PATHS.people,
        ...guarded('people'),
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('../../../pages/people/people.component').then((m) => m.PeopleComponent),
      },
      {
        // After the static `people` route so the directory still wins.
        matcher: personDetailsMatcher,
        ...guarded('people'),
        loadComponent: () =>
          import('../../../pages/people/person-details/person-details.component').then(
            (m) => m.PersonDetailsComponent
          ),
      },
      {
        matcher: teamDetailsMatcher,
        ...guarded('teams'),
        loadComponent: () =>
          import('../../../pages/teams/team-details/team-details.component').then((m) => m.TeamDetailsComponent),
      },
      {
        matcher: projectDetailsMatcher,
        ...guarded('projects'),
        loadComponent: () =>
          import('../../../pages/projects/project-details/project-details.component').then(
            (m) => m.ProjectDetailsComponent
          ),
      },
      {
        path: APP_ROUTE_PATHS.management,
        ...guarded('management-overview'),
        loadComponent: () =>
          import('../../../pages/management/management-overview.component').then(
            (m) => m.ManagementOverviewComponent
          ),
      },
      {
        path: APP_ROUTE_PATHS.managementChargeability,
        ...guarded('management-chargeability'),
        loadComponent: () =>
          import('../../../pages/management/management.component').then((m) => m.ManagementComponent),
      },
      {
        path: APP_ROUTE_PATHS.managementNewOffers,
        ...guarded('management-new-offers', { offerKind: OfferReportKind.New }),
        loadComponent: loadOfferReport,
      },
      {
        path: APP_ROUTE_PATHS.managementLostOffers,
        ...guarded('management-lost-offers', { offerKind: OfferReportKind.Lost }),
        loadComponent: loadOfferReport,
      },
      {
        // Open offers share the new-offers grant; the report has no page key of its own.
        path: APP_ROUTE_PATHS.managementOpenOffers,
        ...guarded('management-new-offers'),
        loadComponent: () =>
          import('../../../pages/management/open-offers/open-offers.component').then((m) => m.OpenOffersComponent),
      },
      {
        path: APP_ROUTE_PATHS.managementTopCustomers,
        ...guarded('management-top-customers'),
        loadComponent: () =>
          import('../../../pages/management/top-customers/top-customers.component').then(
            (m) => m.TopCustomersComponent
          ),
      },
      {
        path: APP_ROUTE_PATHS.managementOpenBudget,
        ...guarded('management-open-budget'),
        loadComponent: () =>
          import('../../../pages/management/open-budget/open-budget.component').then((m) => m.OpenBudgetComponent),
      },
      {
        // Budget alerts share the open-budget grant; the report has no page key of its own.
        path: APP_ROUTE_PATHS.managementBudgetAlerts,
        ...guarded('management-open-budget'),
        loadComponent: () =>
          import('../../../pages/management/budget-alerts/budget-alerts.component').then(
            (m) => m.BudgetAlertsComponent
          ),
      },
      {
        path: APP_ROUTE_PATHS.managementRemainingWork,
        ...guarded('management-remaining-work'),
        loadComponent: () =>
          import('../../../pages/management/remaining-work/remaining-work.component').then(
            (m) => m.RemainingWorkComponent
          ),
      },
      {
        path: APP_ROUTE_PATHS.settings,
        ...guarded('settings'),
        loadComponent: () => import('../../../pages/settings/settings.component').then((m) => m.SettingsComponent),
      },
      {
        path: APP_ROUTE_PATHS.userManagement,
        ...guarded('user-management'),
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('../../../pages/user-management/user-management.component').then((m) => m.UserManagementComponent),
      },
      {
        path: APP_ROUTE_PATHS.feedback,
        ...guarded('feedback-inbox'),
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('../../../pages/feedback/feedback.component').then((m) => m.FeedbackComponent),
      },
      {
        // Shown under the requested address when a guarded page is denied.
        path: APP_ROUTE_PATHS.accessDenied,
        loadComponent: () =>
          import('src/app/shell/access-state/access-state.component').then((m) => m.AccessStateComponent),
      },
      // @template-anchor:shell-routes
      // @template-demo:start demo-routes
      {
        path: APP_ROUTE_PATHS.demo,
        ...guarded(BUILT_IN_ADMINISTRATOR),
        loadComponent: () => import('../../../pages/demo/demo.component').then((m) => m.DemoComponent),
      },
      {
        path: APP_ROUTE_PATHS.restricted,
        ...guarded(BUILT_IN_ADMINISTRATOR),
        loadComponent: () =>
          import('../../../pages/restricted/restricted.component').then((m) => m.RestrictedComponent),
      },
      // @template-demo:end demo-routes
      // @template-demo:start theming-route
      {
        path: APP_ROUTE_PATHS.theming,
        ...guarded(BUILT_IN_ADMINISTRATOR),
        loadComponent: () => import('../../../pages/theming/theming.component').then((m) => m.ThemingComponent),
      },
      // @template-demo:end theming-route
      // The shell route matches on a prefix, so this wildcard — not the top-level one — catches unknown URLs.
      { path: '**', ...landing() },
    ],
  },
  { path: '**', redirectTo: APP_ROUTE_PATHS.root },
];
